<!-- 
Dialog for changing the invoice addressee for a provider.

This is done by creating or updating a "ChangeInvoiceAddressee" override.
-->
<template>
  <v-dialog :value="show" persistent content-class="dialog lg">
    <v-card>
      <v-toolbar color="secondary" flat dark>
        <v-toolbar-title>{{ title }}</v-toolbar-title>
      </v-toolbar>
      <our-busy-indicator :show="saving"/>
      <our-loading-indicator v-if="loading"/>
      <v-card-text v-else>
        <v-form ref="form">
          <v-container grid-list-md pa-0>
            <v-layout wrap>
              <p class="body-2">
                The LTPS system will normally address the invoice PDF to the provider's CEO. If this needs to be overridden
                then you can enter a new addressee here. Enter multiple lines if required.
              </p>
              <v-flex xs12>
                <v-textarea 
                  :value="override.invoiceAddressee" 
                  :rules="[rules.required]"
                  auto-grow
                  label="Invoice addressee*" rows="2" class="mt-2" 
                  hint="Overrides the addressee section of the invoice PDF"
                  maxlength="300"
                  @input="update('invoiceAddressee', $event)" 
                />
              </v-flex>
              <v-flex xs12>
                <v-textarea 
                  :value="override.explanation" 
                  :rules="[rules.required]"
                  auto-grow
                  label="Explanation*" rows="3" class="mt-2"
                  hint="The explanation is for internal use only and will not be shown to the provider"
                  @input="update('explanation', $event)" 
                />
              </v-flex>
            </v-layout>
          </v-container>
          <our-required-field-indicator/>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer/>
        <v-btn :disabled="busy" flat @click="hide">Cancel</v-btn>
        <v-btn :disabled="busy" flat color="success" @click="save">Save</v-btn>
      </v-card-actions>
    </v-card>
    <our-validation-dialog v-model="showServerValidationDialog" :state-error="state.error" />
    <our-validation-dialog v-model="showFormValidationDialog" :form="$refs.form" />
    <our-validation-dialog v-model="showSpecifiedValidationDialog" :specified-error="specifiedValidationError" />
  </v-dialog>
</template>

<script>
import _ from "underscore";
import * as action from "@/store/levy-collection-overrides/action-types";
import * as mutation from "@/store/levy-collection-overrides/mutation-types";
import * as lcpAction from "@/store/levy-collection-providers/action-types";
import lookupMixins from "@/mixins/lookup.mixins";
import ruleMixins from "@/mixins/rule.mixins";

export default {
  mixins: [ruleMixins, lookupMixins],
  model: {
    prop: "show",
    event: "update:show"
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    levyCollectionId: {
      validator: prop => typeof prop === "number" || prop === null,
      required: true
    },
    providerId: {
      validator: prop => typeof prop === "number" || prop === null,
      default: null
    }
  },
  data() {
    return {
      overrideId: null,
      showFormValidationDialog: false,
      showServerValidationDialog: false,
      showSpecifiedValidationDialog: false,
      specifiedValidationError: null
    };
  },
  computed: {
    title() {
      if (this.isNew) {
        return this.selectedProviderName
          ? `Adjust levy calculations for ${this.selectedProviderName}`
          : "Adjust levy calculations";
      } else {
        return this.state.loading || !this.override.providerName
          ? "Adjust levy calculations"
          : `Adjust levy calculations for ${this.override.providerName}`;
      }
    },
    state() {
      return this.$store.state.levyCollectionOverrides.edit;
    },
    override() {
      return this.state.data;
    },
    lcpState() {
      return this.$store.state.levyCollectionProviders;
    },
    busy() {
      return this.loading || this.saving;
    },
    loading() {
      return this.state.loading || this.lcpState.view.loading;
    },
    saving() {
      return this.state.saving;
    },
    hasChosenProvider() {
      return this.override.providerId != null;
    },
    administrativeFeeOverridden() {
      return this.override.administrativeFeeOverrideType == "OverrideAmount";
    },
    riskPremiumOverridden() {
      return this.override.riskPremiumOverrideType == "OverrideAmount";
    },
    specialTuitionProtectionOverridden() {
      return this.override.specialTuitionProtectionOverrideType == "OverrideAmount";
    },
    overrideTypes() {
      const blank = [{ text: "", value: null }];
      return blank.concat(this.lookups.overrideTypes);
    },
    isNew() {
      return !this.overrideId;
    },
    hasCompletedCalculation() {
      return (
        this.override.administrativeFeeOverrideType != null ||
        this.override.specialTuitionProtectionOverrideType != null ||
        this.override.riskPremiumOverrideType != null
      );
    }
  },
  watch: {
    async show(nowShowing) {
      if (nowShowing) {
        this.$refs.form.reset();
        await this.$store.dispatch(lcpAction.VIEW_REFRESH, {
          levyCollectionId: this.levyCollectionId,
          providerId: this.providerId
        });
        const override = _(this.$store.state.levyCollectionProviders.view.data.overrides).find(
          o => o.category == "ChangeInvoiceAddressee"
        );
        this.overrideId = override ? override.id : null;
        if (this.isNew) {
          this.$store.commit(mutation.EDIT_CLEAR);
          this.$refs.form.reset();
        } else {
          await this.$store.dispatch(action.EDIT_GET, {
            levyCollectionId: this.levyCollectionId,
            levyCollectionOverrideId: this.overrideId
          });
        }
      }
    }
  },
  methods: {
    update(field, newValue) {
      this.$store.commit(mutation.EDIT_SET_DATA, { [field]: newValue });
    },
    hide() {
      this.$emit("update:show", false);
    },
    validate() {
      if (!this.$refs.form.validate()) {
        this.showFormValidationDialog = true;
        return false;
      }
      return true;
    },
    async save() {
      if (this.validate()) {
        this.update("category", "ChangeInvoiceAddressee");
        this.update("providerId", this.providerId);
        const saved = await this.$store.dispatch(action.EDIT_SAVE, this.levyCollectionId);
        if (saved) {
          this.finish();
        } else {
          this.showServerValidationDialog = true;
        }
      }
    },
    finish() {
      this.$emit("changed");
      this.hide();
    }
  }
};
</script>
